import React, { useEffect, useRef, useState } from 'react';
import WaveSurfer from 'wavesurfer.js';
import { Play, Pause, SkipBack, SkipForward, Volume2, VolumeX, ChevronDown, ChevronUp, X } from 'lucide-react';
import { Slider } from '@/components/ui/slider';

const formatTime = (t) => {
  if (!t || isNaN(t)) return '0:00';
  const m = Math.floor(t / 60);
  const s = Math.floor(t % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
};

const AudioPlayer = ({ currentTrack, onNext, onPrevious, onClose }) => {
  const containerRef = useRef(null);
  const wsRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(0.7);
  const [isMuted, setIsMuted] = useState(false);
  const [minimized, setMinimized] = useState(false);
  const [wsReady, setWsReady] = useState(false);

  // Init / re-init WaveSurfer whenever the track changes
  useEffect(() => {
    if (!currentTrack) return;
    if (wsRef.current) {
      try { wsRef.current.destroy(); } catch (e) { /* noop */ }
      wsRef.current = null;
    }
    if (!containerRef.current) return;

    setWsReady(false);
    const ws = WaveSurfer.create({
      container: containerRef.current,
      waveColor: '#3f3f46',
      progressColor: '#00F0FF',
      cursorColor: 'rgba(255,255,255,0.6)',
      cursorWidth: 1,
      barWidth: 2,
      barGap: 2,
      barRadius: 2,
      height: 44,
      normalize: true,
    });

    ws.load(currentTrack.preview_url);
    ws.on('ready', () => {
      setDuration(ws.getDuration());
      ws.setVolume(volume);
      setWsReady(true);
      ws.play();
      setIsPlaying(true);
    });
    ws.on('audioprocess', () => setCurrentTime(ws.getCurrentTime()));
    ws.on('seeking', () => setCurrentTime(ws.getCurrentTime()));
    ws.on('play', () => setIsPlaying(true));
    ws.on('pause', () => setIsPlaying(false));
    ws.on('finish', () => {
      setIsPlaying(false);
      if (onNext) onNext();
    });
    ws.on('error', (e) => { console.error('WaveSurfer error', e); });

    wsRef.current = ws;

    return () => {
      try { ws.destroy(); } catch (e) { /* noop */ }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTrack?.preview_url]);

  // Sync volume when slider changes
  useEffect(() => {
    if (wsRef.current) {
      try { wsRef.current.setVolume(isMuted ? 0 : volume); } catch (e) {}
    }
  }, [volume, isMuted]);

  const togglePlay = () => {
    if (!wsRef.current) return;
    if (isPlaying) wsRef.current.pause();
    else wsRef.current.play();
  };

  const toggleMute = () => {
    if (isMuted) {
      setIsMuted(false);
      setVolume(volume === 0 ? 0.7 : volume);
    } else {
      setIsMuted(true);
    }
  };

  if (!currentTrack) return null;

  return (
    <div
      className={`fixed left-0 right-0 z-50 bg-[#0B0B0F]/95 backdrop-blur-xl border-t border-[#27272A] transition-all duration-300 ${minimized ? 'bottom-0' : 'bottom-0'}`}
      data-testid="audio-player"
    >
      <div className="max-w-screen-2xl mx-auto px-4 md:px-6 py-3">
        {/* MINIMIZED VIEW */}
        {minimized ? (
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded overflow-hidden bg-[#121214] flex-shrink-0">
              {currentTrack.album_cover && (
                <img src={currentTrack.album_cover} alt="" className="w-full h-full object-cover" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium truncate" data-testid="player-track-title">{currentTrack.title}</div>
              <div className="text-xs text-[#A1A1AA] truncate">{currentTrack.artist}</div>
            </div>
            <button
              onClick={togglePlay}
              className="w-9 h-9 rounded-full bg-[#00F0FF] hover:bg-[#33F3FF] flex items-center justify-center transition-all hover:scale-105"
              data-testid="player-play-pause-button"
              aria-label={isPlaying ? 'Pause' : 'Play'}
            >
              {isPlaying ? <Pause size={16} fill="#000" stroke="#000" /> : <Play size={16} fill="#000" stroke="#000" className="ml-0.5" />}
            </button>
            <button
              onClick={() => setMinimized(false)}
              className="w-9 h-9 rounded-full text-[#A1A1AA] hover:text-white hover:bg-[#18181B] flex items-center justify-center transition-colors"
              data-testid="player-expand-button"
              aria-label="Expand player"
            >
              <ChevronUp size={18} />
            </button>
            {onClose && (
              <button
                onClick={onClose}
                className="w-9 h-9 rounded-full text-[#A1A1AA] hover:text-white hover:bg-[#18181B] flex items-center justify-center transition-colors"
                data-testid="player-close-button"
                aria-label="Close player"
              >
                <X size={18} />
              </button>
            )}
          </div>
        ) : (
          /* EXPANDED VIEW */
          <>
            <div className="flex items-center gap-4">
              {/* Track info */}
              <div className="flex items-center gap-3 w-56 md:w-64 flex-shrink-0">
                <div className="w-12 h-12 rounded overflow-hidden bg-[#121214] flex-shrink-0">
                  {currentTrack.album_cover && (
                    <img src={currentTrack.album_cover} alt="" className="w-full h-full object-cover" />
                  )}
                </div>
                <div className="min-w-0">
                  <div className="text-sm font-medium truncate" data-testid="player-track-title">{currentTrack.title}</div>
                  <div className="text-xs text-[#A1A1AA] truncate">{currentTrack.artist}</div>
                  <div className="flex gap-2 mt-0.5 text-[10px]">
                    {currentTrack.bpm != null && (
                      <span className="px-1.5 py-0.5 bg-[#18181B] border border-[#27272A] rounded text-[#A1A1AA]" data-testid="player-bpm">
                        {currentTrack.bpm} BPM
                      </span>
                    )}
                    {currentTrack.key && (
                      <span className="px-1.5 py-0.5 bg-[#18181B] border border-[#27272A] rounded text-[#A1A1AA]" data-testid="player-key">
                        Key {currentTrack.key}
                      </span>
                    )}
                    {currentTrack.version && (
                      <span className="px-1.5 py-0.5 bg-[#18181B] border border-[#27272A] rounded text-[#A1A1AA] truncate">
                        {currentTrack.version}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Prev / Play / Next */}
              <div className="flex items-center gap-2 flex-shrink-0">
                <button
                  onClick={onPrevious}
                  className="text-[#A1A1AA] hover:text-white transition-colors w-9 h-9 flex items-center justify-center"
                  data-testid="player-previous-button"
                  aria-label="Previous"
                >
                  <SkipBack size={18} />
                </button>
                <button
                  onClick={togglePlay}
                  className="w-11 h-11 rounded-full bg-[#00F0FF] hover:bg-[#33F3FF] flex items-center justify-center transition-transform hover:scale-105"
                  data-testid="player-play-pause-button"
                  aria-label={isPlaying ? 'Pause' : 'Play'}
                >
                  {isPlaying ? <Pause size={20} fill="#000" stroke="#000" /> : <Play size={20} fill="#000" stroke="#000" className="ml-0.5" />}
                </button>
                <button
                  onClick={onNext}
                  className="text-[#A1A1AA] hover:text-white transition-colors w-9 h-9 flex items-center justify-center"
                  data-testid="player-next-button"
                  aria-label="Next"
                >
                  <SkipForward size={18} />
                </button>
              </div>

              {/* Waveform */}
              <div className="flex-1 min-w-0 flex items-center gap-3">
                <span className="text-[11px] text-[#A1A1AA] w-9 text-right">{formatTime(currentTime)}</span>
                <div className="flex-1 relative">
                  <div ref={containerRef} className="w-full" data-testid="player-waveform" />
                  {!wsReady && (
                    <div className="absolute inset-0 flex items-center">
                      <div className="w-full h-1 rounded-full bg-[#27272A] overflow-hidden">
                        <div className="h-full w-1/3 bg-[#00F0FF]/30 animate-pulse" />
                      </div>
                    </div>
                  )}
                </div>
                <span className="text-[11px] text-[#A1A1AA] w-9">{formatTime(duration)}</span>
              </div>

              {/* Volume */}
              <div className="hidden md:flex items-center gap-2 w-28 flex-shrink-0">
                <button
                  onClick={toggleMute}
                  className="text-[#A1A1AA] hover:text-white transition-colors w-8 h-8 flex items-center justify-center"
                  data-testid="player-volume-button"
                  aria-label={isMuted ? 'Unmute' : 'Mute'}
                >
                  {isMuted || volume === 0 ? <VolumeX size={18} /> : <Volume2 size={18} />}
                </button>
                <Slider
                  value={[isMuted ? 0 : volume]}
                  max={1}
                  step={0.01}
                  onValueChange={(v) => { setVolume(v[0]); if (v[0] > 0) setIsMuted(false); }}
                  className="flex-1"
                  data-testid="player-volume-slider"
                />
              </div>

              {/* Minimize / Close */}
              <div className="flex items-center gap-1 flex-shrink-0">
                <button
                  onClick={() => setMinimized(true)}
                  className="w-9 h-9 rounded-full text-[#A1A1AA] hover:text-white hover:bg-[#18181B] flex items-center justify-center transition-colors"
                  data-testid="player-minimize-button"
                  aria-label="Minimize player"
                >
                  <ChevronDown size={18} />
                </button>
                {onClose && (
                  <button
                    onClick={onClose}
                    className="w-9 h-9 rounded-full text-[#A1A1AA] hover:text-white hover:bg-[#18181B] flex items-center justify-center transition-colors"
                    data-testid="player-close-button"
                    aria-label="Close player"
                  >
                    <X size={18} />
                  </button>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AudioPlayer;
